import { usePackageStore } from './package'

/**
 * Pinia store for flight-related state and functionality
 * Manages search parameters, filters, and selected areas
 */
export const useFlightStore = defineStore('FlightStore', {
  state: () => ({
    // Default search parameters from package
    defaultSearchParams: {},

    // Current search parameters
    searchParams: {
      adults: 2,
      children: 0,
      departureDate: null,
      cities: [],
      maxChanges: 0,
      outwardDestination: null,
      outwardOrigin: null,
      returnDate: null,
      returnDestination: null,
      returnOrigin: null
    },

    // Search state
    searchExtended: false,

    // Filter state
    filter: {
      area: null,
      country: null
    },

    // Selected area IDs for shared state between components
    selectedAreaIds: []
  }),

  getters: {
    /**
     * Get all current filters as an object
     */
    getAllFilters: state => () => ({
      maxStops: state.searchParams.maxChanges,
      country: state.filter.country,
      cities: state.searchParams.cities
    })
  },

  actions: {
    /**
     * Initialize default search parameters from package store
     */
    setDefaultSearchParams() {
      const defaultSearchParams = usePackageStore().defaultServices?.flight?.searchParams
      if (defaultSearchParams) {
        defaultSearchParams.cities = []
        this.defaultSearchParams = defaultSearchParams
        this.searchParams = { ...defaultSearchParams }
      }

      // Update with current passenger data from booking store
      const { getApiPassengerData } = usePassengerData()
      const { adults, children } = getApiPassengerData()
      this.searchParams.adults = adults
      this.searchParams.children = children
    },

    /**
     * Set maximum number of flight changes/stops
     */
    setMaxChanges(maxChanges) {
      this.searchParams.maxChanges = maxChanges
      this.searchExtended = true
    },

    /**
     * Set filter city (legacy method)
     */
    setFilterCity(city = null) {
      this.filter.city = city
      this.searchExtended = true
    },

    /**
     * Set filter country
     */
    setFilterCountry(country = null) {
      this.filter.country = country
      this.searchExtended = true
    },

    /**
     * Set filter area (region/airport group)
     */
    setFilterArea(area = null) {
      this.filter.area = area
      this.searchExtended = true
    },

    /**
     * Set cities/airports for search
     */
    setCities(cities = []) {
      this.searchParams.cities = cities
    },

    /**
     * Set selected area IDs (shared state between components)
     */
    setSelectedAreaIds(ids) {
      this.selectedAreaIds = ids
    },

    /**
     * Update passenger data in search parameters
     */
    updatePassengerData(adults, children) {
      this.searchParams.adults = adults
      this.searchParams.children = children
    },

    /**
     * Sync passenger data with current booking store values
     */
    syncPassengerData() {
      const { getApiPassengerData } = usePassengerData()
      const { adults, children } = getApiPassengerData()
      this.updatePassengerData(adults, children)
    }
  }
})
